import { useState } from "react";
import { api } from "@/lib/api";
import { patternAck } from "@/components/app/RealisationsPatternCard";

export function PatternEvidence({ courses, testId }) {
  if (!courses?.length) return null;
  return (
    <ul className="mt-2 space-y-0.5 text-sm text-stone-600" data-testid={testId}>
      {courses.map((c) => <li key={c}>· {c}</li>)}
    </ul>
  );
}

// One answered "A pattern is emerging" question in the closed review list; correction goes through the same endpoint.
export function PatternAnswerItem({ item, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(null);
  const [failed, setFailed] = useState(null);
  const id = item.candidate_id;

  const choose = async (code) => {
    if (saving) return;
    setSaving(code);
    setFailed(null);
    try {
      const { data } = await api.post("/realisations/patterns/" + id + "/respond", { response: code });
      setEditing(false);
      onChanged(data?.item || item, data?.changed === false ? "Your answer is unchanged." : patternAck(code, data?.action, data?.reflected_in_current_read));
    } catch {
      setFailed(code);
    } finally {
      setSaving(null);
    }
  };

  return (
    <li className="py-3" data-testid={"pattern-previous-" + id}>
      <div className="text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]" data-testid={"pattern-previous-kicker-" + id}>
        {item.kind === "noticing" ? "What I'm noticing" : "A pattern is emerging"}
      </div>
      <div className="mt-1 text-sm font-semibold text-stone-900" data-testid={"pattern-previous-question-" + id}>{item.question}</div>
      <PatternEvidence courses={item.evidence_courses} testId={"pattern-previous-evidence-" + id} />
      <div className="mt-1 text-xs text-stone-500">{item.helper_text}</div>
      <div className="mt-1 text-xs text-stone-600" data-testid={"pattern-previous-state-" + id}>
        {item.response ? "You said: " + item.label + ". " : ""}{item.outcome}
      </div>
      <button type="button" onClick={() => setEditing((v) => !v)} aria-expanded={editing} data-testid={"pattern-change-" + id}
        className="mt-1 min-h-9 text-xs font-semibold text-[#4C1D95] underline-offset-2 hover:underline">
        {editing ? "Keep this answer" : "Change my answer"}
      </button>
      {editing && (
        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={"Change your answer: " + item.statement}>
          {item.options.map((o) => (
            <button key={o.code} type="button" disabled={Boolean(saving)} onClick={() => choose(o.code)}
              aria-pressed={item.response === o.code} data-testid={"pattern-respond-" + o.code + "-" + id}
              className={"min-h-9 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] " +
                (item.response === o.code ? "border-[#4C1D95] bg-violet-50 text-[#4C1D95]" : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")}>
              {saving === o.code ? "Saving…" : o.label}
            </button>
          ))}
        </div>
      )}
      {failed && (
        <div role="alert" className="mt-2 flex flex-wrap items-center gap-3 text-xs text-rose-700" data-testid={"pattern-error-" + id}>
          We couldn't save that just now — nothing has been changed.
          <button type="button" onClick={() => choose(failed)} data-testid={"pattern-retry-" + id}
            className="font-semibold underline underline-offset-2">Try again</button>
        </div>
      )}
    </li>
  );
}
